import { MODULES, MODULE_LABELS, RETURN_TYPE_LABELS, type Module, canAccessModule, phoneLabel } from "@clientflow/shared";
import { ask, askText } from "@/components/dialogs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Building2, Calculator, Wallet, Copy, FolderOpen, Inbox, Landmark, LayoutGrid, LineChart, Mail, MessageSquare, MoreHorizontal, Phone, Plus, Smartphone, Receipt, User } from "lucide-react";
import { useEffect, useRef, useState, type Ref } from "react";
import { Link, useSearch } from "wouter";
import { ClientEmails } from "@/components/client-email";
import { Empty, Loading, PageHeader, Section, useServerPaged } from "@/components/page";
import { InvoicesPanel } from "@/components/billing";
import { ClientBillingProfile, OffboardButton, ReviewOptOut } from "@/components/lifecycle";
import { BOOKS_SECTIONS, BooksTab } from "@/components/books";
import { CFO_SECTIONS, CfoTab } from "@/components/cfo";
import { ClientNav } from "@/components/client-nav";
import { DocumentsPanel, NoThreadSelected, RequestsPanel, ThreadList, ThreadView, type ThreadSummary } from "@/components/panels";
import { ConsentsPanel, FilingsPanel, OrganizersPanel, SignaturesPanel } from "@/components/tax-panels";
import { DocumentFigures } from "@/components/document-figures";
import { FeeField } from "@/components/time-tracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatPeriod } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import type { Client, Engagement, Template } from "@/lib/types";
import { StageBadge } from "@/pages/staff/work";
import { BusinessWorkpaper } from "@/components/business-workpaper";
import { ClientCompliancePanel } from "@/components/entity-compliance";
import { EstimatesPanel, NoticesPanel } from "@/components/estimates-notices";
import { ClientTaxPlans } from "@/pages/staff/tax-planning";
import { PayrollTab } from "@/components/payroll";
import { useMembers } from "@/lib/members";
import { cn } from "@/lib/utils";
import { ClientAi } from "@/pages/staff/ai";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
import { OnboardingTracker } from "@/pages/staff/onboarding";
import { useToast } from "@/lib/use-toast";

export type ClientDetail = {
  client: Client;
  services: Array<{ id: string; module: Module; level: string | null; status: string; startedOn: string; monthlyFeeCents?: number | null }>;
  related: Array<{ id: string; displayName: string; kind: string }>;
  relationships: Array<{ id: string; type: string; fromClientId: string; toClientId: string; ownershipPct: string | null }>;
  portalUsers: Array<{ id: string; userId?: string; name?: string; email?: string; access: "full" | "documents_only"; active: boolean }>;
  counts: { open_engagements: number; open_requests: number; documents: number };
};

export function Services({ id, data, canEdit }: { id: string; data: ClientDetail; canEdit: boolean }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (v: { module: Module; status: "active" | "ended" }) => api.send("POST", `/api/clients/${id}/services`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/clients/", id] }),
  });
  const active = new Map(data.services.filter((s) => s.status !== "ended").map((s) => [s.module, s]));
  return (
    <div className="divide-y divide-border rounded-lg border border-border">
      {MODULES.map((m) => {
        const s = active.get(m);
        // Monthly accounting, payroll and CFO plans carry a monthly fee (recurring revenue).
        const monthly = !!s && m !== "tax";
        return (
          <div key={m} className="px-4 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className={`h-2 w-2 shrink-0 rounded-full ${s ? "bg-success" : "bg-muted-foreground/30"}`} aria-hidden />
              <div className="min-w-0">
              <p className="text-sm font-medium">{MODULE_LABELS[m]}</p>
              <p className="text-xs text-muted-foreground">{s ? `Engaged since ${formatDate(s.startedOn)}` : "Not engaged"}</p>
              </div>
            </div>
            {canEdit && (
              <Button
                size="sm"
                variant={s ? "ghost" : "outline"}
                className="shrink-0 whitespace-nowrap"
                onClick={() => {
                  if (s && !window.confirm(`End ${MODULE_LABELS[m]} for ${data.client.displayName}? Staff who work only in ${MODULE_LABELS[m]} will no longer see this client.`)) return;
                  set.mutate({ module: m, status: s ? "ended" : "active" });
                }}
              >
                {s ? "End service" : "Add service"}
              </Button>
            )}
          </div>
          {s && (m === "bookkeeping" || m === "advisory") && (
            <label className="mt-2 flex items-center gap-2 pl-[1.125rem] text-xs text-muted-foreground">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={!(data.client.portalHiddenModules ?? []).includes(m)}
                onChange={(e) => {
                  const now = data.client.portalHiddenModules ?? [];
                  const next = e.target.checked ? now.filter((x) => x !== m) : [...now, m];
                  api.send("PATCH", `/api/clients/${id}`, { portalHiddenModules: next }).then(() => qc.invalidateQueries({ queryKey: ["/api/clients/", id] }));
                }}
              />
              Show {m === "bookkeeping" ? "the financial figures on Financial Reports" : "the KPIs, plans and aging on the Performance Dashboard"} in the client's portal
            </label>
          )}
          {monthly && (
            <div className="mt-2 pl-[1.125rem]">
              <FeeField
                id={`plan-fee-${m}`}
                label="Monthly fee"
                suffix="per month"
                cents={s.monthlyFeeCents}
                disabled={!canEdit}
                onSave={(monthlyFeeCents) => api.send("POST", `/api/clients/${id}/services`, { module: m, monthlyFeeCents }).then(() => qc.invalidateQueries({ queryKey: ["/api/clients/", id] }))}
              />
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
}

export function PortalAccess({ id, data, canEdit }: { id: string; data: ClientDetail; canEdit: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [email, setEmail] = useState(data.client.email ?? "");
  const [name, setName] = useState(data.client.kind === "individual" ? data.client.displayName : "");
  const [link, setLink] = useState<string | null>(null);
  const invite = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ inviteUrl: string | null }>("POST", `/api/clients/${id}/portal-users`, { email, name }),
    onSuccess: (r) => {
      setLink(r.inviteUrl);
      qc.invalidateQueries({ queryKey: ["/api/clients/", id] });
      if (!r.inviteUrl) toast({ title: "Invitation sent", description: "The invitation will appear the next time this person signs in." });
    },
  });
  const setAccess = useMutation({
    mutationFn: (v: { linkId: string; access: "full" | "documents_only" }) => api.send("PATCH", `/api/clients/${id}/portal-users/${v.linkId}`, { access: v.access }),
  });
  const revoke = useMutation({ mutationFn: (linkId: string) => api.send("DELETE", `/api/clients/${id}/portal-users/${linkId}`) });
  return (
    <div className="space-y-3">
      {data.portalUsers.length === 0 ? (
        <Empty>No one has portal access for this client.</Empty>
      ) : (
        <ul className="space-y-1 text-sm">
          {data.portalUsers.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-3 py-2">
              <span className="min-w-[12rem] flex-1">
                <span className="block font-medium">{p.name}</span>
                <span className="block break-all text-xs text-muted-foreground">{p.email}</span>
              </span>
              <span className="text-xs text-muted-foreground">{p.active ? "Active" : "Invited"}</span>
              {canEdit && (
                <>
                  <select
                    aria-label={`Access for ${p.name}`}
                    value={p.access}
                    onChange={(e) => setAccess.mutate({ linkId: p.id, access: e.target.value as "full" })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  >
                    <option value="full">Full access</option>
                    <option value="documents_only">Documents only</option>
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void ask({ title: `Remove ${p.name}'s portal access?`, description: "They will be signed out of the portal for this client.", confirmLabel: "Remove access", destructive: true }).then((ok) => ok && revoke.mutate(p.id));
                    }}
                  >
                    Remove access
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form
          className="grid gap-2 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            invite.mutate();
          }}
        >
          <Input aria-label="Name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input aria-label="Email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Button type="submit" variant="outline" className="sm:col-span-2" disabled={invite.isPending}>
            Invite to portal
          </Button>
        </form>
      )}
      {invite.error && <p className="text-sm text-destructive">{errorMessage(invite.error)}</p>}
      {link && (
        <div className="rounded-lg border border-accent/60 bg-accent/10 p-3 text-sm">
          <p className="mb-2 font-medium">Send this sign-up link to the client. It is valid for 7 days and is shown only once.</p>
          <div className="flex gap-2">
            <Input readOnly value={link} onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => navigator.clipboard.writeText(link)} aria-label="Copy link">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientWork({ id, canEdit, formRef }: { id: string; canEdit: boolean; formRef?: Ref<HTMLSelectElement> }) {
  const qc = useQueryClient();
  const { data: page, isLoading } = useQuery<{ items: Engagement[] }>({ queryKey: ["/api/engagements?clientId=", id] });
  const work = page?.items;
  const { data: templates } = useQuery<Template[]>({ queryKey: ["/api/templates"] });
  const [templateId, setTemplateId] = useState("");
  const [period, setPeriod] = useState("");
  const [reviewerUserId, setReviewerUserId] = useState("");
  const [yearEndMonth, setYearEndMonth] = useState("12");
  const members = useMembers();
  const chosen = templates?.find((t) => t.id === templateId);
  // A tax workflow that doesn't name its form (e.g. "1120-S / 1065") asks for it.
  const [form, setForm] = useState("");
  const needsForm = chosen?.module === "tax" && !chosen.returnType;
  const returnType = chosen?.returnType ?? (needsForm && form ? form : null);
  // Stage names come from each engagement's workflow template.
  const stageNames = new Map((work ?? []).map((e) => [e.id, templates?.find((t) => t.id === e.templateId)?.stages.find((st) => st.key === e.stageKey)?.name]));
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send("POST", "/api/engagements", {
        clientId: id,
        templateId,
        period: period || undefined,
        returnType: needsForm && form ? form : undefined,
        reviewerUserId: returnType && reviewerUserId ? reviewerUserId : undefined,
        yearEndMonth: returnType && returnType !== "1040" && yearEndMonth !== "12" ? Number(yearEndMonth) : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/engagements?clientId=", id] });
      qc.invalidateQueries({ queryKey: ["/api/requests?clientId=", id] });
      setTemplateId("");
      setForm("");
      setReviewerUserId("");
      setYearEndMonth("12");
    },
  });
  return (
    <div className="space-y-4">
      {canEdit && (
        <form
          className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(9rem,1fr)_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <select ref={formRef} aria-label="Engagement type" required value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Select an engagement type</option>
            {templates?.map((t) => (
              <option key={t.id} value={t.id}>
                {MODULE_LABELS[t.module]} — {t.name}
              </option>
            ))}
          </select>
          <Input
            aria-label="Period"
            placeholder={returnType ? "Tax year, e.g. 2026" : "e.g. 2026-10"}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
          <Button type="submit" disabled={!templateId || create.isPending}>
            Create engagement
          </Button>
          {needsForm && (
            <label className="space-y-1 text-sm sm:col-span-3">
              <span className="text-xs text-muted-foreground">Which return? (sets the due date and the review steps)</span>
              <select aria-label="Return form" required value={form} onChange={(e) => setForm(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm sm:w-80">
                <option value="">Choose the form</option>
                {(Object.entries(RETURN_TYPE_LABELS) as Array<[string, string]>).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {returnType && (
            <div className="grid gap-2 sm:col-span-3 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Reviewer (signs off before the client signs)</span>
                <select aria-label="Reviewer" value={reviewerUserId} onChange={(e) => setReviewerUserId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">Choose later</option>
                  {members
                    .filter((m) => m.isActive && m.role !== "client" && m.role !== "readonly")
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                </select>
              </label>
              {returnType !== "1040" && (
                <label className="space-y-1 text-sm">
                  <span className="text-xs text-muted-foreground">Tax year ends</span>
                  <select aria-label="Tax year ends" value={yearEndMonth} onChange={(e) => setYearEndMonth(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                    {MONTHS.map((m, i) => (
                      <option key={m} value={String(i + 1)}>
                        {i === 11 ? "December (calendar year)" : `${m} (fiscal year)`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <p className="text-xs text-muted-foreground sm:col-span-2">The due date is set from the return type and tax year, moved to the next business day when it falls on a weekend or holiday.</p>
            </div>
          )}
        </form>
      )}
      {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
      {isLoading ? (
        <Loading />
      ) : !work?.length ? (
        <Empty>No open engagements.</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {work.map((e) => (
            <li key={e.id}>
              <Link href={`/engagements/${e.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{e.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {MODULE_LABELS[e.module]}
                    {e.dueDate && ` · Due ${formatDate(e.dueDate)}`}
                  </span>
                </span>
                {stageNames.get(e.id) && <StageBadge name={stageNames.get(e.id)!} />}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const REL_LABELS: Record<string, [string, string]> = { spouse: ["Spouse", "Spouse"], owner_of: ["Owns", "Owned by"], dependent: ["Dependent", "Claims as dependent"], related: ["Related", "Related"] };

function RelatedClients({ id, data, canEdit }: { id: string; data: ClientDetail; canEdit: boolean }) {
  const [v, setV] = useState({ toClientId: "", type: "owner_of" });
  const [q, setQ] = useState("");
  // Search as you type: a firm has hundreds of clients.
  const { data: all } = useQuery<{ items: Array<{ id: string; displayName: string }> }>({ queryKey: ["/api/clients", `?limit=50${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`], enabled: canEdit, placeholderData: (prev) => prev });
  const add = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/clients/${id}/relationships`, v), onSuccess: () => setV({ toClientId: "", type: "owner_of" }) });
  const remove = useMutation({ mutationFn: (rid: string) => api.send("DELETE", `/api/clients/${id}/relationships/${rid}`) });
  const setShare = useMutation({ meta: { inlineError: true }, mutationFn: (x: { rid: string; ownershipPct: string | null }) => api.send("PATCH", `/api/clients/${id}/relationships/${x.rid}`, { ownershipPct: x.ownershipPct }) });
  const pct = (v: string | null) => (v === null ? null : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 })}%`);
  const name = (cid: string) => data.related.find((r) => r.id === cid)?.displayName;
  return (
    <div className="space-y-3">
      {data.relationships.length === 0 ? (
        <p className="text-sm text-muted-foreground">No related clients.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {data.relationships.map((r) => {
            const other = r.fromClientId === id ? r.toClientId : r.fromClientId;
            const label = REL_LABELS[r.type]?.[r.fromClientId === id ? 0 : 1] ?? r.type;
            return (
              <li key={r.id} className="flex items-center gap-2 py-1.5">
                <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
                <Link href={`/clients/${other}`} className="min-w-0 flex-1 truncate hover:underline">
                  {name(other) ?? "A client you can't view"}
                </Link>
                {r.type === "owner_of" &&
                  (canEdit ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="tabular"
                      title="The owner's percentage of the business, used to carry the business's forecast profit into the owner's estimated tax payments"
                      onClick={async () => {
                        const v = await askText({
                          title: "Ownership percentage",
                          description: "The owner's share of the business, for example 50 or 33.3333. Leave empty if not known.",
                          label: "Percentage owned",
                          defaultValue: r.ownershipPct === null ? "" : String(Number(r.ownershipPct)),
                          validate: (t) => (!t.trim() || (/^\d{1,3}(\.\d{1,4})?$/.test(t.trim()) && Number(t) > 0 && Number(t) <= 100) ? null : "Enter a percentage more than 0 and up to 100, with at most four decimals."),
                          confirmLabel: "Save",
                        });
                        if (v !== null) setShare.mutate({ rid: r.id, ownershipPct: v.trim() || null });
                      }}
                    >
                      {pct(r.ownershipPct) ?? "Set share"}
                    </Button>
                  ) : (
                    <span className="tabular text-xs text-muted-foreground">{pct(r.ownershipPct) ?? "Share not recorded"}</span>
                  ))}
                {canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(r.id)} aria-label="Remove link">
                    Remove
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canEdit && (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <select aria-label="Relationship" className="h-9 min-w-0 rounded-md border border-input bg-background px-2 text-sm" value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })}>
            <option value="owner_of">Owns the business</option>
            <option value="spouse">Spouse of</option>
            <option value="dependent">Has dependent</option>
            <option value="related">Related to</option>
          </select>
          <Input aria-label="Search clients" placeholder="Search clients" className="min-w-0" value={q} onChange={(e) => setQ(e.target.value)} />
          <select aria-label="Client" required className="h-9 min-w-0 rounded-md border border-input bg-background px-2 text-sm" value={v.toClientId} onChange={(e) => setV({ ...v, toClientId: e.target.value })}>
            <option value="">{q.trim() ? "Choose from the matches" : "Choose a client"}</option>
            {(all?.items ?? [])
              .filter((c) => c.id !== id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
          </select>
          <Button type="submit" size="sm" variant="outline" className="h-9" disabled={!v.toClientId || add.isPending}>
            Link clients
          </Button>
        </form>
      )}
      {(add.error ?? setShare.error) && <p className="text-sm text-destructive">{errorMessage(add.error ?? setShare.error)}</p>}
    </div>
  );
}

function ClientMessages({ id, me }: { id: string; me: Me }) {
  const qc = useQueryClient();
  const { offset, size, paged } = useServerPaged(id);
  const { data } = useQuery<{ items: ThreadSummary[]; total: number }>({ queryKey: ["/api/threads?clientId=", id, `&offset=${offset}&limit=${size}`], placeholderData: (prev) => prev });
  const [selected, setSelected] = useState<string>();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const start = useMutation({
    mutationFn: () => api.send<{ id: string }>("POST", "/api/threads", { clientId: id, subject, body }),
    onSuccess: (t) => {
      setSubject("");
      setBody("");
      setSelected(t.id);
      qc.invalidateQueries({ queryKey: ["/api/threads?clientId=", id] });
    },
  });
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="min-w-0 space-y-3">
        <ThreadList threads={data?.items ?? []} selected={selected} onSelect={setSelected} paged={paged(data?.total ?? 0)} />
        <form
          className="space-y-2 rounded-lg border border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate();
          }}
        >
          <p className="text-sm font-medium">New message</p>
          <Input aria-label="Subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
          <Textarea aria-label="Message" placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} required rows={3} />
          <Button type="submit" size="sm" disabled={start.isPending}>
            Send
          </Button>
        </form>
      </div>
      <div className="min-h-[300px] min-w-0 rounded-lg border border-card-border bg-card p-4">
        {selected ? <ThreadView id={selected} meId={me.user?.id} /> : <NoThreadSelected />}
      </div>
      <Section title="Email" description="Emails the team exchanged with this client, copied from each person's connected Gmail. Only the firm sees these." className="min-w-0 lg:col-span-2">
        <ClientEmails me={me} clientId={id} />
      </Section>
    </div>
  );
}

/** Name, email and both phone numbers, editable in place. */
function ContactDetails({ client, canEdit }: { client: Client; canEdit: boolean }) {
  const init = { displayName: client.displayName, legalName: client.legalName ?? "", email: client.email ?? "", phone: client.phone ?? "", mobilePhone: client.mobilePhone ?? "" };
  const [v, setV] = useState(init);
  const [editing, setEditing] = useState(false);
  useEffect(() => setV(init), [client.id, client.displayName, client.legalName, client.email, client.phone, client.mobilePhone]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("PATCH", `/api/clients/${client.id}`, { displayName: v.displayName.trim(), legalName: v.legalName.trim() || null, email: v.email.trim() || null, phone: v.phone.trim() || null, mobilePhone: v.mobilePhone.trim() || null }),
    onSuccess: () => setEditing(false),
  });
  const business = client.kind === "business";
  const rows: Array<[keyof typeof v, string, string]> = [
    ["displayName", "Name", "text"],
    ["legalName", business ? "Legal name" : "Full legal name", "text"],
    ["email", "Email", "email"],
    ["phone", business ? "Office phone" : "Phone", "tel"],
    ["mobilePhone", "Mobile", "tel"],
  ];
  if (!editing)
    return (
      <div className="space-y-3 text-sm">
        <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[max-content_1fr]">
          {rows.map(([k, label]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="min-w-0 break-words">
                {!v[k] ? (
                  <span className="text-muted-foreground">—</span>
                ) : k === "email" ? (
                  <a className="text-primary hover:underline" href={`mailto:${v[k]}`}>
                    {v[k]}
                  </a>
                ) : k === "phone" || k === "mobilePhone" ? (
                  <a className="text-primary hover:underline" href={`tel:${v[k]}`}>
                    {v[k]}
                  </a>
                ) : (
                  v[k]
                )}
              </dd>
            </div>
          ))}
        </dl>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            Edit contact details
          </Button>
        )}
      </div>
    );
  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map(([k, label, type]) => (
          <div key={k} className="space-y-1">
            <label htmlFor={`cd-${k}`} className="text-xs font-medium">
              {label}
            </label>
            <Input id={`cd-${k}`} type={type} required={k === "displayName"} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      {save.error && <p className="text-destructive">{errorMessage(save.error)}</p>}
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button size="sm" type="button" variant="ghost" onClick={() => (setV(init), setEditing(false))}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ClientCode({ id, code, canEdit }: { id: string; code: string | null; canEdit: boolean }) {
  const [value, setValue] = useState(code ?? "");
  const save = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("PATCH", `/api/clients/${id}`, { clientCode: value.trim() || null }) });
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-muted-foreground">The client ID used in your tax software (for example, OLT Pro). Used to match client imports and bulk document imports.</p>
      <div className="flex gap-2">
        <Input aria-label="Client code" placeholder="e.g. 1042" value={value} disabled={!canEdit} onChange={(e) => setValue(e.target.value)} />
        {canEdit && (
          <Button type="submit" variant="outline" disabled={save.isPending || value === (code ?? "")}>
            Save
          </Button>
        )}
      </div>
      {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
    </form>
  );
}

type TextStatus = { phone: string | null; consentAt: string | null; stopped: boolean; recent: Array<{ kind: string; status: string; createdAt: string; error: string | null }> };
const TEXT_KIND: Record<string, string> = { confirmation: "Confirmation", reminder: "Reminder", cancellation: "Cancellation" };

/** Whether the client agreed to texts (only they can say yes), and the last few sent. Staff can turn them off. */
function ClientTexts({ id, canEdit }: { id: string; canEdit: boolean }) {
  const { data } = useQuery<TextStatus>({ queryKey: [`/api/clients/${id}/texts`] });
  const off = useMutation({ mutationFn: () => api.send("POST", `/api/clients/${id}/texts-off`) });
  if (!data) return <Loading />;
  return (
    <div className="space-y-3 text-sm">
      {data.phone ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p>
            <span className="font-medium">On</span> for {phoneLabel(data.phone)}
            <span className="block text-xs text-muted-foreground">{data.stopped ? "They replied STOP: no texts are sent until they reply START." : `They agreed when booking on ${formatDate(data.consentAt!)}.`}</span>
          </p>
          {canEdit && (
            <Button size="sm" variant="outline" disabled={off.isPending} onClick={() => void ask({ title: "Turn off texts?", description: "No more texts to this client. Only they can turn texts back on, when they next book.", confirmLabel: "Turn off", destructive: true }).then((ok) => ok && off.mutate())}>
              Turn off
            </Button>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground">Off. Clients turn texts on themselves by ticking “Text me” when they book.</p>
      )}
      {data.recent.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {data.recent.map((m, i) => (
            <li key={i} className="flex flex-wrap items-center justify-between gap-x-3 px-3 py-1.5 text-xs">
              <span>
                {TEXT_KIND[m.kind] ?? m.kind} · {formatDate(m.createdAt)}
              </span>
              <span className={cn("text-muted-foreground", m.status === "failed" && "text-destructive")}>{m.status === "sent" ? "Sent" : m.status === "queued" ? "Waiting to send" : (m.error ?? m.status)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Less common client actions, kept out of the header's main buttons. */
function MoreMenu({ id, status, canArchive, canEdit }: { id: string; status: Client["status"]; canArchive: boolean; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const archived = status === "archived";
  const act = useMutation({ mutationFn: () => api.send("POST", `/api/clients/${id}/${archived ? "unarchive" : "archive"}`) });
  const toOnboarding = useMutation({ mutationFn: () => api.send("PATCH", `/api/clients/${id}`, { status: "prospect" }) });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const canOnboard = canEdit && (status === "active" || status === "inactive");
  if (!canArchive && !canOnboard) return null;
  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="icon" aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-popover-border bg-popover p-1 shadow-lg">
          {canOnboard && (
            <button
              role="menuitem"
              className="w-full rounded px-3 py-2 text-left text-sm hover:bg-muted"
              onClick={() => {
                setOpen(false);
                void ask({ title: "Move back to onboarding?", description: "The client shows as a prospect with the onboarding checklist until you mark onboarding complete. Nothing else changes.", confirmLabel: "Move to onboarding" }).then((ok) => ok && toOnboarding.mutate());
              }}
            >
              Move back to onboarding
            </button>
          )}
          {canArchive && (
            <button
              role="menuitem"
              className={`w-full rounded px-3 py-2 text-left text-sm hover:bg-muted ${archived ? "" : "text-destructive"}`}
              onClick={() => {
                setOpen(false);
                if (archived) act.mutate();
                else void ask({ title: "Archive this client?", description: "Open engagements and requests will be cancelled and portal access will end. All records are retained.", confirmLabel: "Archive client", destructive: true }).then((ok) => ok && act.mutate());
              }}
            >
              {archived ? "Restore client" : "Archive client"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_LABELS: Record<Client["status"], string> = { prospect: "Prospect", active: "Active", inactive: "Inactive", archived: "Archived" };
const OLD_TABS: Record<string, string> = { work: "overview", sign: "documents", consents: "tax" };

export function ClientDetailPage({ id, me }: { id: string; me: Me }) {
  const search = new URLSearchParams(useSearch());
  const requested = search.get("tab") ?? "overview";
  const [tab, setTab] = useState(OLD_TABS[requested] ?? requested);
  const [section, setSection] = useState(search.get("section") ?? "dashboard");
  // The address follows the section, so a link or a refresh opens the same place.
  const go = (t: string, sec?: string) => {
    setTab(t);
    if (sec) setSection(sec);
    const q = new URLSearchParams(window.location.search);
    q.set("tab", t);
    if ((t === "cfo" || t === "books") && sec) q.set("section", sec);
    else q.delete("section");
    window.history.replaceState(null, "", `${window.location.pathname}?${q}`);
  };
  const newEngagement = useRef<HTMLSelectElement>(null);
  const { data, isLoading, error } = useQuery<ClientDetail>({ queryKey: ["/api/clients/", id] });
  const { data: organizers } = useQuery<Array<{ kind?: string }>>({ queryKey: ["/api/organizers?clientId=", id] });
  const canEdit = hasPermission(me, "clients.write");
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>Client not found.</Empty>;
  const c = data.client;
  const hasIntake = !!organizers?.some((o) => o.kind === "intake");
  const engaged = (m: Module) => data.services.some((sv) => sv.module === m && sv.status !== "ended");
  // A link to a service tab this client isn't engaged for opens the overview instead of a blank page.
  const SERVICE_TABS: Record<string, Module> = { tax: "tax", books: "bookkeeping", payroll: "payroll", cfo: "advisory" };
  const shownTab = SERVICE_TABS[tab] && !engaged(SERVICE_TABS[tab]!) ? "overview" : tab;
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Clients", href: "/clients" }]}
        // From one of the client's sections, Back returns to the client's overview.
        back={shownTab !== "overview" ? { label: `${c.displayName}'s overview`, href: `/clients/${id}` } : undefined}
        title={
          <span className="flex items-center gap-2.5">
            {c.kind === "business" ? <Building2 className="h-5 w-5 text-muted-foreground" /> : <User className="h-5 w-5 text-muted-foreground" />}
            {c.displayName}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.status === "active" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-muted text-muted-foreground"}`}>
              {STATUS_LABELS[c.status]}
            </span>
            {c.email && (
              <span className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> {c.email}
              </span>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:underline">
                <Phone className="h-3.5 w-3.5" /> {c.phone}
              </a>
            )}
            {c.mobilePhone && (
              <a href={`tel:${c.mobilePhone}`} className="flex items-center gap-1 hover:underline">
                <Smartphone className="h-3.5 w-3.5" /> {c.mobilePhone}
              </a>
            )}
          </span>
        }
        actions={
          <>
            {hasPermission(me, "engagements.write") && c.status !== "archived" && (
              <Button
                onClick={() => {
                  go("overview");
                  setTimeout(() => newEngagement.current?.focus(), 0);
                }}
              >
                <Plus className="h-4 w-4" /> New engagement
              </Button>
            )}
            {hasPermission(me, "requests.write") && (
              <Button variant="outline" onClick={() => go("requests")}>
                Send request
              </Button>
            )}
            <Button variant="outline" onClick={() => go("messages")}>
              Message
            </Button>
            <MoreMenu id={id} status={c.status} canArchive={hasPermission(me, "clients.archive")} canEdit={canEdit} />
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[208px_minmax(0,1fr)]">
        <ClientNav
          current={shownTab}
          section={section}
          onSelect={go}
          items={[
            { key: "overview", label: "Overview", icon: LayoutGrid },
            ...([
              ["tax", "tax", "Tax", Calculator],
              ["bookkeeping", "books", "Accounting Services", BookOpen],
              ["payroll", "payroll", "Payroll", Wallet],
              ["advisory", "cfo", "CFO & Advisory", LineChart],
            ] as const)
              .filter(([m]) => canAccessModule(me.role, me.staffModules, m))
              .map(([m, key, label, icon]) => ({
                key,
                label,
                icon,
                group: "Services",
                disabled: !engaged(m),
                title: engaged(m) ? undefined : `${c.displayName} is not engaged for ${MODULE_LABELS[m]}`,
                children: key === "cfo" ? CFO_SECTIONS : key === "books" ? BOOKS_SECTIONS : undefined,
              })),
            ...(c.kind === "business" && (canAccessModule(me.role, me.staffModules, "tax") || canAccessModule(me.role, me.staffModules, "bookkeeping")) ? [{ key: "compliance", label: "Entity & Sales Tax", icon: Landmark, group: "Services" }] : []),
            { key: "requests", label: "Requests", icon: Inbox, group: "Client", count: data.counts.open_requests },
            { key: "documents", label: "Documents", icon: FolderOpen, group: "Client", count: data.counts.documents },
            { key: "messages", label: "Messages", icon: MessageSquare, group: "Client" },
            ...(hasPermission(me, "billing.read") ? [{ key: "billing", label: "Billing", icon: Receipt, group: "Client" }] : []),
          ]}
        />
      <Tabs value={shownTab} onValueChange={(t) => go(t)} className="min-w-0">
        <TabsContent value="overview" className="mt-0 grid gap-6 lg:grid-cols-2">
          <OnboardingTracker clientId={id} me={me} onOpenTab={(t) => go(t)} />
          <Section title="Engagements" className="lg:col-span-2">
            <ClientWork id={id} canEdit={hasPermission(me, "engagements.write")} formRef={newEngagement} />
          </Section>
          {(c.status === "prospect" || hasIntake) && (
            <Section title="Intake forms" className="lg:col-span-2">
              <OrganizersPanel clientId={id} me={me} kind="intake" />
            </Section>
          )}
          <Section title="Contact details">
            <ContactDetails client={c} canEdit={canEdit} />
          </Section>
          <Section title="Engaged services">
            <Services id={id} data={data} canEdit={canEdit} />
          </Section>
          <Section id="portal-access" title="Client portal access" className="scroll-mt-24">
            <PortalAccess id={id} data={data} canEdit={canEdit} />
          </Section>
          <Section title="Client code">
            <ClientCode id={id} code={c.clientCode} canEdit={canEdit} />
          </Section>
          <Section title="AI">
            <ClientAi clientId={id} canEdit={canEdit} me={me} />
          </Section>
          <Section title="Text reminders">
            <ClientTexts id={id} canEdit={canEdit} />
          </Section>
          {(data.related.length > 0 || canEdit) && (
            <Section title="Related clients" description="Spouses, dependents and the businesses a client owns, with the owner's share. A tax plan offers an owner's businesses for QuickBooks figures, and their estimated payments follow the businesses' forecasts.">
              <RelatedClients id={id} data={data} canEdit={canEdit} />
            </Section>
          )}
          {c.notes && (
            <Section title="Notes">
              <p className="whitespace-pre-wrap text-sm">{c.notes}</p>
            </Section>
          )}
        </TabsContent>
        {engaged("tax") && (
        <TabsContent value="tax" className="mt-0 space-y-6">
          <Section title="Returns, extensions and refunds">
            {/* An individual's returns (federal and each state, every year) fill about a box; a business usually has fewer. */}
            <FilingsPanel clientId={id} me={me} loadingSize={c.kind === "individual" ? "box" : undefined} />
          </Section>
          {/* Personal planning and 1040-ES estimates: a business's owners have them on their own records. */}
          {c.kind === "individual" && (
            <>
              <Section title="Tax planning" description="Multi-year projections and strategies, reviewed and then shared for the client to accept.">
                <ClientTaxPlans clientId={id} clientName={c.displayName} me={me} />
              </Section>
              <Section title="Estimated tax payments" description="Safe harbor from last year's return; the client sees the schedule and confirms each payment in the portal.">
                <EstimatesPanel clientId={id} me={me} />
              </Section>
            </>
          )}
          {c.kind === "business" && (
            <Section title="Return workpaper" description="The year-end trial balance from QuickBooks grouped into the return's lines, with the M-1 and M-2 checks.">
              <BusinessWorkpaper clientId={id} me={me} />
            </Section>
          )}
          <Section title="IRS & state notices">
            <NoticesPanel clientId={id} me={me} />
          </Section>
          <Section title="Tax organizers">
            <OrganizersPanel clientId={id} me={me} />
          </Section>
          <Section
            title="Figures from documents"
            description="Each W-2, 1099, 1098 and other form the client sent, as confirmed in Review › Documents."
            action={
              <Link href={`/clients/${id}/tax-input`} className="shrink-0 text-sm font-medium text-primary hover:underline">
                Tax input sheet
              </Link>
            }
          >
            <DocumentFigures clientId={id} />
          </Section>
          <Section title="§7216 consents">
            <ConsentsPanel clientId={id} me={me} />
          </Section>
        </TabsContent>
        )}
        {c.kind === "business" && (
          <TabsContent value="compliance" className="mt-0">
            <ClientCompliancePanel clientId={id} me={me} />
          </TabsContent>
        )}
        <TabsContent value="requests" className="mt-0">
          <RequestsPanel clientId={id} canEdit={hasPermission(me, "requests.write")} />
        </TabsContent>
        <TabsContent value="documents" className="mt-0 space-y-6">
          <Section title="Documents">
            <DocumentsPanel clientId={id} canEdit={hasPermission(me, "documents.write")} lockable={hasPermission(me, "billing.write")} />
          </Section>
          <Section title="E-signatures" description="Engagement letters, proposals, consents and IRS e-file authorizations (Forms 8879 and 8878).">
            <SignaturesPanel clientId={id} me={me} portalUsers={data.portalUsers} />
          </Section>
        </TabsContent>
        <TabsContent value="messages" className="mt-0">
          <ClientMessages id={id} me={me} />
        </TabsContent>
        {hasPermission(me, "billing.read") && (
          <TabsContent value="billing" className="mt-0">
            <div className="space-y-6">
              <ClientBillingProfile clientId={id} me={me} onboarding={data.client.status === "prospect"} />
              <Section title="Invoices" description="Every invoice issued to this client, with payments.">
                <InvoicesPanel clientId={id} me={me} startNew={search.get("new") === "1"} />
              </Section>
              <Section
                title="Client relationship"
                description="Review requests, and ending some or all of the services."
                action={hasPermission(me, "clients.archive") && data.client.status !== "archived" && <OffboardButton clientId={id} clientName={data.client.displayName} />}
              >
                <ReviewOptOut clientId={id} optOut={!!data.client.reviewOptOut} />
              </Section>
            </div>
          </TabsContent>
        )}
        {engaged("bookkeeping") && (
          <TabsContent value="books" className="mt-0">
            <BooksTab clientId={id} me={me} section={section} />
          </TabsContent>
        )}
        {engaged("payroll") && (
          <TabsContent value="payroll" className="mt-0">
            <PayrollTab clientId={id} me={me} />
          </TabsContent>
        )}
        {engaged("advisory") && (
          <TabsContent value="cfo" className="mt-0">
            <CfoTab clientId={id} me={me} section={section} onSection={(sec) => go("cfo", sec)} />
          </TabsContent>
        )}
      </Tabs>
      </div>
    </>
  );
}
